<template>
	<teleport to="body">
		<div class="notification-container">
			<transition-group name="toast" tag="div">
				<div
					v-for="notification in notifications"
					:key="notification.id"
					:class="['toast', `toast-${notification.type}`]"
					role="alert"
					:aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
				>
					<!-- Иконка -->
					<div class="toast-icon">
						<svg
							v-if="notification.type === 'success'"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
							<polyline points="22 4 12 14.01 9 11.01"></polyline>
						</svg>

						<svg
							v-else-if="notification.type === 'error'"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<circle cx="12" cy="12" r="10"></circle>
							<line x1="15" y1="9" x2="9" y2="15"></line>
							<line x1="9" y1="9" x2="15" y2="15"></line>
						</svg>

						<svg
							v-else-if="notification.type === 'warning'"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path
								d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
							></path>
							<line x1="12" y1="9" x2="12" y2="13"></line>
							<line x1="12" y1="17" x2="12.01" y2="17"></line>
						</svg>

						<svg
							v-else
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<circle cx="12" cy="12" r="10"></circle>
							<line x1="12" y1="16" x2="12" y2="12"></line>
							<line x1="12" y1="8" x2="12.01" y2="8"></line>
						</svg>
					</div>

					<!-- Контент -->
					<div class="toast-content">
						<div v-if="notification.title" class="toast-title">
							{{ notification.title }}
						</div>
						<div class="toast-message">{{ notification.message }}</div>
					</div>

					<!-- Кнопка закрытия -->
					<button
						class="toast-close"
						@click="removeNotification(notification.id)"
						aria-label="Закрыть уведомление"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>
			</transition-group>
		</div>
	</teleport>
</template>

<script>
import { computed } from 'vue'
import { useAppStore } from '@/stores/useAppStore'

export default {
	name: 'NotificationToast',
	setup() {
		const appStore = useAppStore()

		// appStore.notifications уже computed, не нужен .value
		const notifications = computed(() => {
			const list = appStore.notifications
			const raw = Array.isArray(list?.value) ? list.value : list
			return (Array.isArray(raw) ? raw : []).filter(
				item => (item?.channel || 'toast') === 'toast',
			)
		})

		const removeNotification = id => {
			appStore.removeNotification(id)
		}

		return {
			notifications,
			removeNotification,
		}
	},
}
</script>

<style scoped>
/* Контейнер для уведомлений */
.notification-container {
	position: fixed;
	top: 20px;
	right: 20px;
	z-index: 100001;
	display: flex;
	flex-direction: column;
	gap: 25px;
	pointer-events: none;
	max-width: 420px;
	width: 100%;
}

/* Toast уведомление */
.toast {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 16px;
	background: white;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
	pointer-events: auto;
	min-width: 320px;
	max-width: 420px;
	font-family: var(--font-family-poppins, 'Poppins', sans-serif);
	transition: all 0.3s ease;
}

.toast:hover {
	box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08);
	transform: translateY(-2px);
}

/* Иконка */
.toast-icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.toast-icon svg {
	width: 100%;
	height: 100%;
}

/* Контент */
.toast-content {
	flex: 1;
	min-width: 0;
}

.toast-title {
	font-weight: 600;
	font-size: 14px;
	line-height: 1.4;
	margin-bottom: 4px;
	color: #252641;
}

.toast-message {
	font-size: 13px;
	line-height: 1.5;
	color: #636363;
	word-wrap: break-word;
}

/* Кнопка закрытия */
.toast-close {
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	color: #636363;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
}

.toast-close:hover {
	background: rgba(0, 0, 0, 0.05);
	color: #252641;
}

.toast-close:active {
	transform: scale(0.9);
}

.toast-close svg {
	width: 16px;
	height: 16px;
}

/* Типы уведомлений */
.toast-success {
	border-left: 4px solid #4caf50;
}

.toast-success .toast-icon {
	color: #4caf50;
}

.toast-error {
	border-left: 4px solid #f44336;
}

.toast-error .toast-icon {
	color: #f44336;
}

.toast-warning {
	border-left: 4px solid #ff9800;
}

.toast-warning .toast-icon {
	color: #ff9800;
}

.toast-info {
	border-left: 4px solid #0095ec;
}

.toast-info .toast-icon {
	color: #0095ec;
}

/* Анимации */
.toast-enter-active,
.toast-leave-active {
	transition: all 0.3s ease;
}

.toast-enter-from {
	opacity: 0;
	transform: translateX(100%);
}

.toast-leave-to {
	opacity: 0;
	transform: translateX(100%) scale(0.8);
}

.toast-move {
	transition: transform 0.3s ease;
}

/* Адаптивность */
@media (max-width: 768px) {
	.notification-container {
		top: 10px;
		right: 10px;
		left: 10px;
		max-width: none;
	}

	.toast {
		min-width: auto;
		max-width: none;
	}
}

/* Accessibility: отключение анимаций */
@media (prefers-reduced-motion: reduce) {
	.toast-enter-active,
	.toast-leave-active,
	.toast-move {
		transition: none !important;
	}

	.toast-enter-from,
	.toast-leave-to {
		opacity: 1 !important;
		transform: none !important;
	}

	.toast:hover {
		transform: none !important;
	}
}
</style>
